import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { Injectable, Injector, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as Sentry from '@sentry/angular';
import { ExternalLogEntryCreateDtoV2, LogsServiceV2 } from 'api-client';

import { LoggingCoreService, LogLevelV2 } from './logging-core.service';
import { environment } from '../../../environments/environment';

export enum LogLevel {
  All = 0,
  Debug = 1,
  Info = 2,
  Warn = 3,
  Error = 4,
  Fatal = 5,
  Off = 6,
}

/**
 * Unified logging service that supports both V1 and V2 logging systems.
 *
 * When V2 logging is enabled (via LOG_CONFIG), logs are sent through the
 * LoggingCoreService for batched submission with correlation tracking.
 *
 * V1 logging is always available as a fallback and for environments
 * where V2 is not configured.
 *
 * The public API remains backward-compatible with the original LogService.
 */
@Injectable({
  providedIn: 'root',
})
export class LogService {
  prodLogMinumumLevel: LogLevel = LogLevel.Error;
  logWithDate = true;

  private injector = inject(Injector);
  private _loggingCore = inject(LoggingCoreService);

  /**
   * Log a debug message.
   *
   * Backward-compatible: Accepts either (msg, sourceContext?, ...params) or (msg, ...params).
   * If the second parameter is a string, it's treated as sourceContext.
   * If the second parameter is not a string, it's treated as the first optional param.
   *
   * @param msg Message to log
   * @param args Either [sourceContext, ...params] or [...params]
   */
  debug(msg: string, ...args: unknown[]) {
    const { sourceContext, params } = this.parseLogArgs(args);
    this.writeToLog(msg, LogLevel.Debug, params, sourceContext);
    this.sendToV2Logging('Debug', msg, sourceContext, params);
  }

  /**
   * Log an info message.
   *
   * Backward-compatible: Accepts either (msg, sourceContext?, ...params) or (msg, ...params).
   *
   * @param msg Message to log
   * @param args Either [sourceContext, ...params] or [...params]
   */
  info(msg: string, ...args: unknown[]) {
    const { sourceContext, params } = this.parseLogArgs(args);
    this.writeToLog(msg, LogLevel.Info, params, sourceContext);
    this.sendToV2Logging('Information', msg, sourceContext, params);
  }

  /**
   * Log a warning message.
   *
   * Backward-compatible: Accepts either (msg, sourceContext?, ...params) or (msg, ...params).
   *
   * @param msg Message to log
   * @param args Either [sourceContext, ...params] or [...params]
   */
  warn(msg: string, ...args: unknown[]) {
    const { sourceContext, params } = this.parseLogArgs(args);
    this.writeToLog(msg, LogLevel.Warn, params, sourceContext);
    this.sendToV2Logging('Warning', msg, sourceContext, params);
  }

  /**
   * Log an error message. Also sends to Sentry.
   *
   * Backward-compatible: Accepts either (msg, sourceContext?, ...params) or (msg, ...params).
   *
   * @param msg Message to log
   * @param args Either [sourceContext, ...params] or [...params]
   */
  error(msg: string, ...args: unknown[]) {
    const { sourceContext, params } = this.parseLogArgs(args);
    this.writeToLog(msg, LogLevel.Error, params, sourceContext);
    this.sendToSentry(msg);
    this.sendToV2Logging('Error', msg, sourceContext, params);
  }

  /**
   * Log a fatal error message. Also sends to Sentry.
   *
   * Backward-compatible: Accepts either (msg, sourceContext?, ...params) or (msg, ...params).
   *
   * @param msg Message to log
   * @param args Either [sourceContext, ...params] or [...params]
   */
  fatal(msg: string, ...args: unknown[]) {
    const { sourceContext, params } = this.parseLogArgs(args);
    this.writeToLog(msg, LogLevel.Fatal, params, sourceContext);
    this.sendToSentry(msg);
    this.sendToV2Logging('Fatal', msg, sourceContext, params);
  }

  /**
   * Parse log arguments for backward compatibility.
   * If the first argument is a string, it's treated as sourceContext.
   * If the first argument is not a string, all arguments are treated as params.
   *
   * @param args Arguments passed after the message
   * @returns Parsed sourceContext and params
   */
  private parseLogArgs(args: unknown[]): { sourceContext?: string; params: unknown[] } {
    if (args.length === 0) {
      return { params: [] };
    }

    const firstArg = args[0];

    // If first arg is a string, treat it as sourceContext
    if (typeof firstArg === 'string') {
      return {
        sourceContext: firstArg,
        params: args.slice(1),
      };
    }

    // Otherwise, all args are params (backward compatibility)
    return {
      params: args,
    };
  }

  /**
   * Log a general message (verbose level).
   *
   * @param msg Message to log
   * @param optionalParams Additional parameters
   */
  log(msg: string, ...optionalParams: unknown[]) {
    this.writeToLog(msg, LogLevel.All, optionalParams);
    this.sendToV2Logging('Verbose', msg, undefined, optionalParams);
  }

  /**
   * Log a message and add it as a Sentry tag.
   *
   * @param msg Tag name
   * @param optionalParams First param is the tag value
   */
  tag(msg: string, ...optionalParams: unknown[]) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
    this.addSentryTag(msg, optionalParams[0] as string);
    this.sendToV2Logging('Information', `[TAG] ${msg}: ${optionalParams[0]}`, 'LogService.tag');
  }

  /**
   * Log a message and add it as Sentry context.
   *
   * @param msg Context name
   * @param optionalParams First param is the context object
   */
  context(msg: string, ...optionalParams: unknown[]) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
    this.addSentryContext(msg, optionalParams[0] as object);
    this.sendToV2Logging('Information', `[CONTEXT] ${msg}`, 'LogService.context', optionalParams);
  }

  /**
   * Log a message and display it as a snackbar toast.
   *
   * @param msg Message to display and log
   * @param optionalParams Additional parameters
   */
  toast(msg: string, ...optionalParams: unknown[]) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
    this.openSnackBar(msg);
    this.sendToV2Logging('Information', `[TOAST] ${msg}`, 'LogService.toast');
  }

  /**
   * Log an error message, display toast, and send to Sentry.
   *
   * @param msg Error message
   * @param optionalParams Additional parameters
   */
  toastError(msg: string, ...optionalParams: unknown[]) {
    this.writeToLog(msg, LogLevel.Error, optionalParams);
    this.openSnackBar(msg);
    this.sendToSentry(msg);
    this.sendToV2Logging('Error', `[TOAST_ERROR] ${msg}`, 'LogService.toastError');
  }

  /**
   * Log a message, display toast, and send to Sentry.
   *
   * @param msg Message
   * @param optionalParams Additional parameters
   */
  toastAndLogToSentry(msg: string, ...optionalParams: unknown[]) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
    this.openSnackBar(msg);
    this.sendToSentry(msg);
    this.sendToV2Logging('Information', msg, 'LogService.toastAndLogToSentry');
  }

  /**
   * Log a message and send to Sentry.
   *
   * @param msg Message
   * @param optionalParams Additional parameters
   */
  logToSentry(msg: string, ...optionalParams: unknown[]) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
    this.sendToSentry(msg);
    this.sendToV2Logging('Information', msg, 'LogService.logToSentry');
  }

  private shouldLog(level: LogLevel): boolean {
    let ret = false;

    if (
      (environment.production && level >= this.prodLogMinumumLevel && level !== LogLevel.Off) ||
      !environment.production
    ) {
      ret = true;
    }

    return ret;
  }

  private writeToLog(msg: string, level: LogLevel, params: unknown[], sourceContext?: string) {
    let value = '';
    let severity = '';

    switch (level) {
      case LogLevel.Debug:
        severity = '[DEBUG]';
        break;
      case LogLevel.Info:
        severity = '[INFO]';
        break;
      case LogLevel.Warn:
        severity = '[WARN]';
        break;
      case LogLevel.Error:
        severity = '[ERROR]';
        break;
      case LogLevel.Fatal:
        severity = '[FATAL]';
        break;
      default:
        severity = '[INFO]';
    }

    const context = this.addContextInfo(value, severity);
    const paramsWithContext = [...params, context];

    value = '[' + environment.name + '][' + environment.env.toUpperCase() + '] ';
    if (sourceContext) {
      value += `[${sourceContext}] `;
    }
    value += severity + ' ' + msg;

    if (paramsWithContext.length > 1) {
      value += ' - Extra Info: ' + this.formatParams(paramsWithContext);
    }

    // Only send to V1 API when V2 logging is disabled (V1 is fallback)
    if (!this._loggingCore.isV2LoggingEnabled()) {
      this.sendToLogs(value, level);
    }

    if (this.shouldLog(level)) {
      /* eslint-disable no-console -- This is a logging service, console output is intentional */
      switch (level) {
        case LogLevel.Debug:
          console.debug(value);
          break;
        case LogLevel.Info:
          console.info(value);
          break;
        case LogLevel.Warn:
          console.warn(value);
          break;
        case LogLevel.Error:
          console.error(value, params);
          break;
        case LogLevel.Fatal:
          console.error(value);
          break;
        default:
          console.log(value);
      }
      /* eslint-enable no-console */
    }
  }

  /**
   * Send log to V2 logging system (if enabled).
   */
  private sendToV2Logging(
    level: LogLevelV2,
    message: string,
    sourceContext?: string,
    params?: unknown[],
  ): void {
    // Only send if V2 logging is enabled
    if (!this._loggingCore.isV2LoggingEnabled()) {
      return;
    }

    // Convert params to properties object
    let properties: Record<string, unknown> | undefined;
    if (params && params.length > 0) {
      properties = {};
      params.forEach((param, index) => {
        if (param !== null && param !== undefined) {
          if (typeof param === 'object' && !Array.isArray(param)) {
            // Merge object properties
            Object.assign(properties!, param as Record<string, unknown>);
          } else {
            // Store primitive or array with index key
            properties![`param_${index}`] = param;
          }
        }
      });
    }

    this._loggingCore.log(level, message, sourceContext, undefined, properties);
  }

  openSnackBar(message: string) {
    const snackBar = this.injector.get(MatSnackBar);
    snackBar.open(message, 'X', {
      duration: 3000,
    });
  }

  addSentryTag(tagName: string, tagValue: string) {
    Sentry.setTag(tagName, tagValue);
  }

  addSentryContext(contextName: string, context: object) {
    Sentry.setContext(contextName, context as Record<string, unknown>);
  }

  sendToSentry(message: string) {
    Sentry.captureMessage(message);
  }

  sendToLogs(value: string, level: number) {
    const logsService = this.injector.get(LogsServiceV2);
    const logEntry: ExternalLogEntryCreateDtoV2 = {
      message: value,
      level: this.mapLogLevelToString(level),
      timestamp: new Date().toISOString(),
      source: environment.name,
      sourceContext: 'LogService',
    };

    logsService.apiV2LogsEntryPost(logEntry).subscribe({
      next: () => {
        // Log sent successfully (V2 API)
      },
      error: (err) => {
        // Silently fail - don't cause cascading log failures
        // Only log to console in development
        if (!environment.production) {
          // eslint-disable-next-line no-console -- Intentional fallback logging for dev debugging
          console.error('[LogService] Failed to send V2 log:', err);
        }
      },
    });
  }

  /**
   * Map numeric log level to V2 API string level.
   */
  private mapLogLevelToString(level: number): string {
    switch (level) {
      case LogLevel.Debug:
        return 'Debug';
      case LogLevel.Info:
        return 'Information';
      case LogLevel.Warn:
        return 'Warning';
      case LogLevel.Error:
        return 'Error';
      case LogLevel.Fatal:
        return 'Fatal';
      default:
        return 'Verbose';
    }
  }

  addContextInfo(message: string, severity: string) {
    const appId = environment.appId;
    const time = new Date();
    const location = this.injector.get(LocationStrategy);
    const url = location instanceof PathLocationStrategy ? location.path() : '';
    const env = environment.env;

    const contextInfo = {
      appId,
      time,
      url,
      env,
      message,
      severity,
    };
    return contextInfo;
  }

  private formatParams(params: unknown[]): string {
    let ret = '';

    for (const item of params) {
      ret += JSON.stringify(item) + ',';
    }

    return ret;
  }
}
